import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import { Spherical, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { useMediaPreferences } from "../../hooks/useMediaPreferences";

export interface OrbitFacing {
  readonly azimuthRadians: number;
  readonly polarRadians?: number;
  /** Bump to re-aim the camera; continuous changes are ignored otherwise. */
  readonly cue: number;
}

export function SimpleOrbitRig({
  facing,
  minDistance,
  maxDistance,
  maxPolarAngleDegrees = 162,
  target = [0, 0, 0],
}: {
  readonly facing?: OrbitFacing;
  readonly minDistance: number;
  readonly maxDistance: number;
  readonly maxPolarAngleDegrees?: number;
  readonly target?: readonly [number, number, number];
}) {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const { prefersReducedMotion } = useMediaPreferences();
  const lookTarget = useMemo(() => new Vector3(...target), [target]);
  // Creation is kept independent of the target so a moving target never
  // recreates the controls (which would discard the user's orientation).
  const controls = useMemo(() => {
    const next = new OrbitControls(camera, gl.domElement);
    next.enablePan = false;
    next.autoRotate = false;
    next.enableDamping = true;
    next.dampingFactor = 0.075;
    next.minDistance = minDistance;
    next.maxDistance = maxDistance;
    next.minPolarAngle = (18 * Math.PI) / 180;
    next.maxPolarAngle = (maxPolarAngleDegrees * Math.PI) / 180;
    next.enableZoom = false;
    return next;
  }, [camera, gl.domElement, maxDistance, maxPolarAngleDegrees, minDistance]);

  useEffect(() => () => controls.dispose(), [controls]);

  useEffect(() => {
    controls.target.copy(lookTarget);
    controls.update();
  }, [controls, lookTarget]);

  const latestFacing = useRef(facing);
  useEffect(() => {
    latestFacing.current = facing;
  }, [facing]);
  const facingCue = facing?.cue;

  useEffect(() => {
    const aim = latestFacing.current;
    if (facingCue === undefined || aim === undefined) {
      return;
    }
    const offset = camera.position.clone().sub(controls.target);
    const spherical = new Spherical().setFromVector3(offset);
    spherical.theta = aim.azimuthRadians;
    if (aim.polarRadians !== undefined) {
      spherical.phi = Math.min(
        Math.max(aim.polarRadians, controls.minPolarAngle),
        controls.maxPolarAngle,
      );
    }
    offset.setFromSpherical(spherical);
    camera.position.copy(controls.target).add(offset);
    controls.update();
  }, [camera, controls, facingCue]);

  useEffect(() => {
    controls.enableDamping = !prefersReducedMotion;
    controls.update();
  }, [controls, prefersReducedMotion]);

  useFrame(() => {
    controls.update();
  });

  return null;
}
